import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { ProjectIssue } from "@/types/api";
import { AddContributorSheet } from "./add-contributor-sheet";
import { LinkReadyDialog } from "./link-ready-dialog";
import { useSendIssueLink } from "./use-send-issue-link";

interface AddContributorButtonProps {
  projectId: string;
  issue: ProjectIssue;
  projectName: string | undefined;
  currentUserId: string | undefined;
  presentation: "drawer" | "dialog";
}

/**
 * **Add contributor**, beside Update in the issue's action bar. The page
 * renders it only when the server says the viewer may bring people on
 * (`viewer_can_invite`).
 *
 * The sheet's two choices come when the server says the viewer may pick from
 * the project (`viewer_can_add_from_project`): today everyone who may invite,
 * contributors included. Otherwise it goes straight to sending a link. See
 * [[add-contributor-sheet.spec]].
 */
export function AddContributorButton({
  projectId,
  issue,
  projectName,
  currentUserId,
  presentation,
}: AddContributorButtonProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const link = useSendIssueLink(projectId, issue);
  const withSheet = Boolean(issue.viewer_can_add_from_project);
  const sendingStraight = !withSheet && link.sending;

  return (
    <>
      <Button
        variant="outline"
        className="h-[46px] w-full"
        aria-busy={sendingStraight}
        onClick={() => {
          if (withSheet) {
            setSheetOpen(true);
          } else if (!link.sending) {
            void link.send();
          }
        }}
      >
        {sendingStraight ? (
          <Loader2 className="animate-spin" aria-hidden="true" />
        ) : (
          <UserPlus aria-hidden="true" />
        )}
        Add contributor
      </Button>

      {withSheet && (
        <AddContributorSheet
          projectId={projectId}
          issue={issue}
          projectName={projectName}
          currentUserId={currentUserId}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          presentation={presentation}
          onSendLink={link.send}
          sending={link.sending}
        />
      )}

      <LinkReadyDialog
        url={link.readyUrl}
        viaShareSheet={link.viaShareSheet}
        onSend={link.retry}
        onDismiss={link.dismissReady}
      />
    </>
  );
}
